import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { catchError, map, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class KeycloakService {
  get dataUrl(): string | null {
    return this._dataUrl;
  }

  set dataUrl(value: string | null) {
    this._dataUrl = value;
  }

  get password(): string | null {
    return this._password;
  }

  set password(value: string | null) {
    this._password = value;
  }

  get loginEventId(): string | null {
    return this._loginEventId;
  }

  set loginEventId(value: string | null) {
    this._loginEventId = value;
  }

  get username(): string | null {
    return this._username;
  }

  set username(value: string | null) {
    this._username = value;
  }

  private _username: string | null = null;
  private _password: string | null = null;
  private _dataUrl: string | null = null;
  private _loginEventId: string | null = null;

  constructor(private httpService: HttpClient) {}

  checkCredentials(username: string, password: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    const body = new URLSearchParams();
    body.set('username', username);
    body.set('password', password);

    return this.httpService
      .post<any>(
        environment.api_host + 'auth/check-credentials',
        body.toString(),
        { headers, observe: 'response' },
      )
      .pipe(
        map((response) =>
          this.handleLoginResponse(response, username, password),
        ),
        catchError((error) => {
          console.error('Error checking credentials:', error);
          throw error; // Rethrow the error if you want to handle it in the component
        }),
      );
  }

  setupTwoFactorAuth(otp: string) {
    if (!this._username || !this._password) {
      return throwError(
        () =>
          new Error(
            'Username and password are required to setup two-factor authentication',
          ),
      );
    }
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    const body = new URLSearchParams();
    body.set('otp', otp);
    body.set('username', this._username);
    body.set('password', this._password);
    if (this._loginEventId) {
      body.set('loginEventId', this._loginEventId);
    }

    return this.httpService
      .post<any>(
        environment.api_host + 'auth/setup-two-factor-auth',
        body.toString(),
        { headers, observe: 'response' },
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          console.error('Error setting up two-factor auth:', error);
          return throwError(() => error);
        }),
      );
  }

  setupEmailTwoFactorAuth(otp: string) {
    if (!this._username || !this._password) {
      return throwError(
        () =>
          new Error(
            'Username and password are required to setup two-factor authentication',
          ),
      );
    }
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    const body = new URLSearchParams();
    body.set('otp', otp.toString());

    body.set('username', this._username.toString());
    body.set('password', this._password.toString());
    if (this._loginEventId) {
      body.set('loginEventId', this._loginEventId);
    }

    return this.httpService
      .post<any>(
        environment.api_host + 'auth/setup-email-otp',
        body.toString(),
        { headers, observe: 'response' },
      )
      .pipe(
        map((response) => {
          console.log('Response:', response);
          return response;
        }),
        catchError((error) => {
          console.error('Error setting up two-factor auth:', error);
          return throwError(() => error);
        }),
      );
  }

  verifyEmailOtp(
    username: string,
    password: string,
    otp: string,
    loginEventId?: string | null,
  ) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    const body = new URLSearchParams();
    body.set('username', username.toString());
    body.set('password', password.toString());
    body.set('otp', otp.toString());
    if (loginEventId) {
      body.set('loginEventId', loginEventId);
    }

    return this.httpService.post<any>(
      environment.api_host + 'auth/emailotp/verify',
      body,
      { headers, observe: 'response' },
    );
  }

  sendEmailOtp(
    username: string,
    password: string,
    loginEventId?: string | null,
  ) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    const body = new URLSearchParams();
    body.set('username', username.toString());
    body.set('password', password.toString());
    if (loginEventId) {
      body.set('loginEventId', loginEventId);
    }

    return this.httpService.post<{ message: string; resendTimeout: number }>(
      environment.api_host + 'auth/send-email-otp',
      body.toString(),
      { headers, observe: 'response' },
    );
  }

  handleLoginResponse(response: any, username: string, password: string) {
    if (response.status === 200) {
      this._username = username;
      this._password = password;
    }
    return response;
  }

  resetLoginFlow() {
    this._username = null;
    this._password = null;
    this._dataUrl = null;
    this._loginEventId = null;
  }

  login(username: string, password: string, otp: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    const body = new URLSearchParams();
    body.set('username', username);
    body.set('password', password);
    body.set('otp', otp);
    return this.httpService.post<any>(
      environment.api_host + 'api/v1/auth/login',
      body.toString(),
      { headers, observe: 'response' },
    );
  }
}
